// [SCOPE 051] Authored en-US locale bundle — the single source of truth for the
// app's user-facing strings. This is the ONLY authored locale in v1 (framework
// only); other locales fall back to en-US per key (FR-002). The bulk string
// extraction across the app (spec 052) fills this out area by area.
//
// Structure: nested namespaces → keys. Reference with dotted paths, e.g.
// t("common.save") or t("settings.language.title"). Interpolate with {{var}}.
//
// Methodology/lifecycle terms (Scope, Stage Gate, Lifecycle Phase, the six phase
// names, Spec) are intentionally NOT translated anywhere (spec 053 FR-003); keep
// them out of this bundle as translatable keys.

export const enUS = {
  common: {
    save: "Save",
    cancel: "Cancel",
    close: "Close",
    delete: "Delete",
    edit: "Edit",
    loading: "Loading…",
    saving: "Saving…",
    saved: "Saved",
    error: "Something went wrong",
    retry: "Retry",
    yes: "Yes",
    no: "No",
  },
  settings: {
    language: {
      title: "Language",
      description: "Choose the language for the app interface.",
      help: "Untranslated languages fall back to English until their strings are authored.",
      saved: "Language updated.",
    },
  },
  landing: {
    nav: {
      home: "Home",
      features: "Features",
      pricing: "Pricing",
      docs: "Help/Tutorials",
      signIn: "Sign In",
    },
    hero: {
      subtitle: "AI Project Management System",
      description:
        "wxKanban is a comprehensive project management system developed around AI with a production-ready starting kit for teams of any size. Built for security, compliance, and seamless integrations.",
      getStarted: "Get Started",
      learnMore: "Learn More",
    },
    install: {
      eyebrow: "Get started in ~30 minutes",
      heading: "Installation & Setup",
      description:
        "New to wxKanban? Our step-by-step guide takes you from a fresh computer to a working setup in VS Code — installing Claude Code, adding the Dev Cockpit, and wiring up your first project. No prior experience needed.",
      step1: {
        title: "Install the tools",
        body: "VS Code, Node.js, and Claude Code — with copy-paste commands.",
      },
      step2: {
        title: "Add the Dev Cockpit",
        body: "Install the extension and connect to your wxKanban project.",
      },
      step3: {
        title: "Run your first workflow",
        body: "Scope, spec, and build — guided through the 6-phase lifecycle.",
      },
      cta: "View setup instructions",
    },
    features: {
      heading: "Powerful Features",
      subheading:
        "Everything you need to manage projects, track time, and collaborate with your team",
      aiPowered: {
        title: "AI-Powered",
        description:
          "Intelligent task suggestions and workflow optimization powered by your favorite AI tool for development",
      },
      security: {
        title: "Enterprise Security",
        description: "SOC 2, HIPAA, and GDPR compliant with AES-256-GCM encryption",
      },
      integration: {
        title: "Multi-System Integration",
        description:
          "Connect Jira, Monday.com, Asana, Trello, and other PM platforms seamlessly",
      },
      conversion: {
        title: "Conversion of Legacy Applications",
        intro: "Convert your legacy applications such as:",
        more: "and more",
      },
      collaboration: {
        title: "Team Collaboration",
        item1: "Real-time team management with role-based access control",
        item2: "Add your company's SDLC documents to be analyzed for compliance",
        item3: "Assign SDLC to each team member to comply with audit controls",
        item4: "Have AI help ensure you comply with all audit requirements",
      },
      lifecycle: {
        title: "6-Phase Lifecycle",
        item1: "Structured quality methodology from Design through Release",
        item2: "Open source commands and Claude to manage the entire development process",
        item3: "Update specs and tasks on your Project Management system automatically",
        item4: "Auto flows to ensure compliance with Audits",
        item5: "Full audit report on each release",
        footer:
          "What you write and generate in VS Code — now in a full project management system",
      },
    },
    pricing: {
      heading: "Simple, Transparent Pricing",
      subheading:
        "Choose the perfect plan for your team. Always with the flexibility to upgrade or downgrade",
      mostPopular: "MOST POPULAR",
      perMonth: "/month",
      free: "Free",
      faqText: "Have questions about pricing?",
      contactSales: "Contact our sales team",
      tiers: {
        single: {
          name: "Single User",
          userLimit: "1 user",
          f1: "Unlimited projects",
          f2: "Single PM integration",
          f3: "Email support",
          cta: "Start Now",
        },
        smb: {
          name: "Small/Medium Business",
          userLimit: "Up to 5 users",
          f1: "SOC 2 & HIPAA certified",
          f2: "Single company",
          f3: "Team management",
          f4: "Email support",
          cta: "Start Now",
        },
        consultant: {
          name: "Consultant",
          userLimit: "Unlimited team",
          f1: "Unlimited team members",
          f2: "Multiple PM connections",
          f3: "Advanced time tracking",
          f4: "Invoice generation",
          f5: "Custom workflows",
          f6: "API access",
          cta: "Register",
        },
        enterprise: {
          name: "Enterprise",
          priceUnit: "+ $5/user over 25",
          userLimit: "25+ users",
          f1: "25 included users",
          f2: "$5/mo per active user above 25",
          f3: "Unlimited everything",
          f4: "Dedicated support",
          f5: "Custom integrations",
          f6: "SLA guarantee",
          cta: "Start Now",
        },
      },
    },
    compliance: {
      heading: "Enterprise-Grade Security",
      subheading: "Built with compliance and security as foundational requirements",
      soc2: { name: "SOC 2 Aligned", description: "Built to SOC 2 Trust Services Criteria" },
      hipaa: {
        name: "HIPAA-Aware Controls",
        description: "Safeguards modeled on protected health information standards",
      },
      gdpr: { name: "GDPR Ready", description: "Privacy and data-protection controls built in" },
      encryption: {
        title: "Encryption at Rest & In Transit",
        description: "AES-256-GCM encryption for all data",
      },
      auth: {
        title: "Secure Authentication",
        description: "bcrypt hashing with JWT tokens and refresh rotation",
      },
      audit: {
        title: "Audit Logging",
        description: "Complete audit trail for compliance and transparency",
      },
      rbac: {
        title: "Role-Based Access Control",
        description: "Fine-grained permissions for teams and data",
      },
      trustLead: "Built for trust",
      trustBody:
        "Your data security is our top priority. We hold ourselves to recognized security standards and audit our controls regularly.",
    },
    cta: {
      headline: "Ready to Transform Your Project Management?",
      subhead: "Join thousands of teams using wxKanban to streamline their workflows",
      getStarted: "Get Started",
      viewPricing: "View Pricing",
    },
    footer: {
      tagline: "AI-powered project management for modern teams",
      product: "Product",
      company: "Company",
      legal: "Legal",
      features: "Features",
      pricing: "Pricing",
      documentation: "Documentation",
      about: "About",
      blog: "Blog",
      contact: "Contact",
      bio: "Bio",
      privacy: "Privacy",
      terms: "Terms",
      security: "Security",
      rights: "© 2026 wxKanban. All rights reserved.",
    },
  },
  appnav: {
    dashboard: "Dashboard",
    teamMembers: "Team Members",
    projects: "Projects",
    pmIntegrations: "PM Integrations",
    myTasks: "My Tasks",
    myDay: "My Day", // [SCOPE 017 / T047]
    customers: "Customers", // [SCOPE 119 / T012]
    workHub: "Work Hub",
    timeBilling: "Time & Billing",
    invoices: "Invoices",
    reports: "Reports",
    stackProfiles: "Stack Profiles",
    settings: "Settings",
    gettingStarted: "Getting Started",
    manual: "Manual", // [SCOPE 141 / T016]
    help: "Help",
    profile: "My Profile",
    signOut: "Sign Out",
  },
  login: {
    title: "Welcome back",
    subtitle: "Sign in to your wxKanban account",
    email: "Email",
    emailPlaceholder: "you@company.com",
    password: "Password",
    rememberMe: "Remember me",
    forgotPassword: "Forgot password?",
    signIn: "Sign In",
    signingIn: "Signing in…",
    genericError: "An unexpected error occurred. Please try again.",
    noAccount: "Don't have an account?",
    register: "Register your company",
    tagline: "Multi-Platform Task Management",
    emailLabel: "Email Address",
    showPasswordAria: "Hold to show password",
    registerLink: "Register here",
    connectPmPrompt: "Or connect a PM system to get started:",
    loginFailed: "Login failed",
  },
  forgotPassword: {
    title: "Forgot Password",
    description: "Enter your email and we'll send you a link to reset your password.",
    successMessage: "If an account with that email exists, a password reset link has been sent. Check your inbox.",
    backToSignIn: "Back to sign in",
    emailLabel: "Email Address",
    emailPlaceholder: "you@example.com",
    submit: "Send Reset Link",
    submitting: "Sending...",
    errorFailed: "Failed to send reset link",
  },
  resetPassword: {
    title: "Reset Password",
    subtitle: "Enter your new password below.",
    newPassword: "New Password",
    confirmNewPassword: "Confirm New Password",
    updatePassword: "Update Password",
    updating: "Updating...",
    successMessage: "Your password has been reset successfully. Redirecting to sign in...",
    backToSignIn: "Back to sign in",
    errMissingLink: "This password reset link is invalid or incomplete.",
    errMissingToken: "Reset token is missing. Please use the link from your email.",
    errPasswordMin: "Password must be at least 8 characters",
    errPasswordMismatch: "Passwords do not match",
    errFailed: "Failed to reset password",
  },
  integrations: {
    title: "PM System Integrations",
    subtitle: "Connect your project management tools to wxKanban",
    viewTasks: "View My Tasks →",
    connectedSuccess: "✓ PM system connected successfully!",
    loading: "Loading integrations...",
    connectedSince: "Connected since {{date}}",
    notConnected: "Not connected",
    active: "✓ Active",
    disconnect: "Disconnect",
    connect: "Connect",
    needHelp: "Need help connecting?",
    helpText: "Each PM system requires OAuth credentials. Check the documentation for setup instructions.",
    confirmDisconnect: "Disconnect {{system}}?",
  },
  invoices: {
    title: "Invoices",
    subtitle: "Generate and manage client invoices from billable time entries",
    generate: "Generate Invoice",
    statTotal: "Total Invoices",
    statRevenue: "Total Revenue",
    statSent: "Sent Invoices",
    modalTitle: "Generate New Invoice",
    modalDesc: "Select date range for billable time entries",
    startDate: "Start Date",
    endDate: "End Date",
    generateConfirm: "Generate",
    history: "Invoice History",
    emptyTitle: "No invoices generated yet",
    emptyHint: "Create your first invoice to get started",
    colNumber: "Invoice #",
    colDate: "Date",
    colBilledTo: "Billed To",
    colHours: "Hours",
    colAmount: "Amount",
    colStatus: "Status",
    colActions: "Actions",
    statusSent: "Sent",
    statusDraft: "Draft",
    na: "N/A",
    detailTitle: "Invoice {{number}}",
    generatedOn: "Generated on {{date}}",
    hoursWorked: "Hours Worked",
    totalAmount: "Total Amount",
    downloadPdf: "Download PDF",
    sendInvoice: "Send Invoice",
    resend: "Resend",
  },
  reports: {
    title: "Reports",
    subtitle: "View detailed analytics on time tracking, projects, and team performance",
    tabTime: "Time Reports",
    tabProjects: "Project Reports",
    dateRange: "Date Range",
    exportCsv: "Export CSV",
    timeByMember: "Time by Team Member",
    timeByMemberDesc: "Breakdown of hours logged by each team member",
    selectDateRange: "Select a date range to view time reports",
    colMember: "Team Member",
    colTotalHours: "Total Hours",
    colBillableHours: "Billable Hours",
    colBillablePct: "Billable %",
    projectPerformance: "Project Performance",
    projectPerformanceDesc: "Overview of all projects and their progress",
    noProjects: "No projects found",
    colProject: "Project",
    colHours: "Hours",
    colTasks: "Tasks",
    colCompletion: "Completion",
    colTeamSize: "Team Size",
  },
  // [SCOPE 119 / T014] Customer records — a consultant's own record of a client.
  customers: {
    title: "Customers",
    subtitle: "Your own record of who you work for. Separate from wxKanban accounts.",
    listHeading: "Customer list",
    add: "Add customer",
    addFirst: "Add your first customer",
    searchPlaceholder: "Search name or company",
    searchLabel: "Search customers",
    archived: "Archived",
    archivedOn: "Archived {{date}}",
    archivedBanner: "This customer is archived. It cannot be assigned to new projects.",
    restore: "Restore",
    edit: "Edit",
    archive: "Archive",
    transferOwner: "Transfer owner",
    unlink: "Unlink",
    link: "Link account",
    linked: "linked",
    contactBilling: "Contact & billing",
    email: "Email",
    phone: "Phone",
    taxid: "Tax ID",
    billingaddress: "Billing address",
    notRecorded: "Not recorded",
    wxkanbanAccount: "wxKanban account",
    linkedAccount: "Linked account",
    linkDisclaimer:
      "Shown for reference only. Linking records that this client is also a wxKanban account — it grants you no access to their data, and grants them no access to yours.",
    engagements: "Engagements",
    engagementsManagedElsewhere: "Managed in Engagements — read-only here.",
    noEngagements: "No engagements with this account yet.",
    engagementState: {
      active: "active",
      paused: "paused",
      disabled: "disabled",
      ended: "ended",
    },
    projectsCount: "Projects · {{count}}",
    noProjects: "No projects assigned to this customer yet.",
    loading: "Loading customers",
    emptyTitle: "No customers yet",
    emptyBody:
      "Add the clients you work for — including the ones who have never heard of wxKanban. A customer here is just your record of them: no invitation is sent and they get no access to anything.",
    errorTitle: "Couldn’t load your customers",
    errorBody:
      "The request failed. Nothing was changed — your customers and their billing details are safe.",
    retry: "Retry",
    // [SCOPE 119 / T016] create / edit form
    newTitle: "New customer",
    editTitle: "Edit customer",
    formHint:
      "Only a name is required. No invitation is sent and the customer gets no access to anything.",
    name: "Name",
    companyField: "Company",
    saving: "Saving…",
    saveFailed: "Couldn’t save this customer.",
    // [SCOPE 119 / T017] transfer dialog
    transferTitle: "Transfer ownership",
    transferBody: "Move “{{name}}” to another consultant in your company.",
    transferTo: "New owner",
    transferChoose: "Choose a consultant…",
    transferConfirm: "Transfer",
    transferring: "Transferring…",
    transferFailed: "Couldn’t transfer this customer.",
    transferNote:
      "Only ownership moves. Projects keep their customer, and past time entries and invoices are not rewritten. The transfer is recorded in the company audit log.",
  },
  // [SCOPE 017 / T111] External PM connections — metadata is public to the
  // company, secrets are not.
  connections: {
    heading: "Connections",
    add: "Add connection",
    none: "No connections to this customer's PM systems yet.",
    test: "Test",
    notYours: "Personal to another consultant",
    scope: {
      PERSONAL: "Personal",
      TEAM: "Team",
    },
    health: {
      healthy: "healthy",
      warning: "warning",
      error: "error",
      unknown: "not checked",
    },
    // [SCOPE 017 / T071] Add-connection form.
    newTitle: "Add a connection",
    formHint: "Link this customer to the PM system you work in for them.",
    nameField: "Name",
    systemType: "System",
    apiEndpoint: "API endpoint",
    apiEndpointHint: "Must start with https:// — your credentials are sent to this address.",
    apiToken: "API token",
    accountEmail: "Account email",
    accountEmailHint: "Only some systems need this alongside the token — Jira does.",
    scopeLabel: "Who can use this credential",
    scopeHint: {
      PERSONAL: "Only you can read or test it. Colleagues see the connection and its health, never the token.",
      TEAM: "Anyone in your company can read, test and rotate it. Use this for a shared service account.",
    },
    afterSaveHint:
      "Saving does not verify the token. Use Test afterwards — it confirms the endpoint answers, not that the credential works.",
    saveFailed: "Could not save the connection.",
    systems: {
      jira: "Jira",
      monday: "Monday.com",
      trello: "Trello",
      asana: "Asana",
      azuredevops: "Azure DevOps",
      custom: "Custom",
    },
  },
  // [SCOPE 017 / T047] Consultant Dashboard — orients; the Work Hub executes.
  consultantdash: {
    title: "Your day",
    open: "Open",
    openWorkHub: "Open Work Hub",
    openWorkHubInstead: "Open Work Hub instead",
    openInWorkHub: "Open in Work Hub",
    today: "Today",
    thisWeek: "This week",
    periodEarnings: "Period earnings",
    entryCount: "{{count}} entries logged",
    billable: "billable",
    atAvgRate: "at {{rate}}/hr average",
    noBillableYet: "no billable time yet",
    unbilled: "unbilled",
    activeProjects: "Active projects",
    customers: "Customers", // [SCOPE 119 / T013]
    addCustomer: "Add customer", // [SCOPE 119 / T013]
    manage: "Manage", // [SCOPE 119 / T013]
    projectCount: "{{count}} projects", // [SCOPE 119 / T013]
    noCustomers: "No customers yet.", // [SCOPE 119 / T013]
    taskCounts: "{{todo}} to do · {{inprogress}} in progress · {{done}} done",
    due: "Due {{date}}",
    dueOverdue: "Due {{date}} · overdue",
    completionOf: "Completion of {{name}}",
    needsAttention: "Needs attention · {{count}}",
    emptyTitle: "Nothing assigned yet",
    emptyBody:
      "This fills in once you are a member of an active project with tasks. In the meantime the Work Hub shows everything you can currently act on.",
    errorTitle: "Couldn’t load your dashboard",
    errorBody:
      "The request failed. Your time entries are safe — nothing was lost. Retrying usually clears it.",
    retry: "Retry",
  },
  workhub: {
    title: "Consultant Work Hub",
    subtitle: "Unified task queue with project filtering and one-click time tracking.",
    allProjects: "All Projects",
    timerRunning: "Timer Running",
    stopTimer: "Stop Timer",
    inactivityTitle: "Timer has been running over 8 hours",
    inactivityBody: "Is this still active work? Stop the timer if you stepped away.",
    keepRunning: "Keep Running",
    loadingQueue: "Loading consultant queue...",
    loadError: "Failed to load consultant work hub data.",
    assignedQueue: "Assigned Work Queue",
    noTasks: "No assigned tasks found for this filter.",
    compliance: "Compliance",
    activity: "Activity",
    blocked: "Blocked",
    suspended: "Suspended",
    stop: "Stop",
    startTimer: "Start Timer",
    activeTask: "Active task",
    billablePreview: "Invoice-Ready Billable Preview",
    complianceFlaggedTasks: "Compliance flagged tasks: {{count}}",
    loadingPreview: "Loading billable preview...",
    previewError: "Unable to load billable preview.",
    previewSummary: "Entries: {{count}} • Total Billable Hours: {{hours}}",
    sourceBreakdown: "Source breakdown:",
    manual: "Manual:",
    ai: "AI:",
    complianceReviewPanel: "Compliance Review Panel",
    loadingFlags: "Loading compliance flags...",
    flagsError: "Unable to load compliance flags.",
    noFlags: "No open compliance flags.",
    summaryTotal: "Total Tasks",
    summaryPastDue: "Past Due",
    summaryAging: "30+ Day Aging",
    changeStatus: "Change Task Status",
    task: "Task",
    newStatus: "New status",
    blockedDesc: "external dependency prevents progress.",
    suspendedDesc: "paused for reassessment.",
    reason: "Reason",
    reasonPlaceholder: "Explain why this task is being paused...",
    reasonRequired: "A reason is required for blocked or suspended tasks.",
    updateStateError: "Failed to update task state",
    unknownError: "Unknown error",
    submit: "Submit",
    saving: "Saving...",
    cancel: "Cancel",
    saveTimeEntry: "Save Time Entry",
    duration: "Duration",
    billableQ: "Billable?",
    billable: "Billable",
    nonBillable: "Non-billable",
    notesOptional: "Notes (optional)",
    notesPlaceholder: "What did you work on?",
    saveEntry: "Save Entry",
    saveEntryError: "Failed to save entry",
    taskActivity: "Task Activity",
    loadingActivity: "Loading task activity...",
    loadDetailError: "Failed to load task detail",
    timeEntries: "Time Entries ({{count}})",
    noTimeEntries: "No time entries yet.",
    invoiced: "Invoiced",
    statusHistory: "Status History ({{count}})",
    noStateChanges: "No state changes recorded.",
    pmSyncState: "PM Sync State",
    syncStatus: "Status:",
    syncUnknown: "unknown",
    lastSyncLabel: "Last sync: {{time}}",
    retries: "Retries: {{count}}",
    errorLabel: "Error: {{message}}",
    resolved: "Resolved: {{time}}",
    aiBadge: "AI",
    manualBadge: "Manual",
    running: "running",
  },
  billing: {
    title: "Billing Configuration",
    subtitle: "Configure hourly rates for time tracking and invoicing. Rates follow a hierarchy: User → Project → Company Default.",
    fetchError: "Failed to fetch billing rates",
    updateError: "Failed to update rate",
    updateSuccess: "Billing rate updated successfully",
    invalidRate: "Please enter a valid rate",
    resolutionTitle: "Rate Resolution Order:",
    resolutionBody: "When calculating billable amounts, the system checks rates in this order: 1) User-specific rate, 2) Project-specific rate, 3) Company default rate.",
    companyRate: "Company Default Rate",
    companyRateDesc: "Fallback rate used when no user or project rate is set",
    hourlyRate: "Hourly Rate (USD)",
    projectRates: "Project-Specific Rates",
    projectRatesDesc: "Override company default for specific projects",
    projectIdLabel: "Project ID: {{id}}",
    userRates: "User-Specific Rates",
    userRatesDesc: "Override all other rates for specific team members",
    noTeamMembers: "No team members found",
    customRate: "Custom Rate",
    usingDefault: "Using Default",
    defaultPlaceholder: "Default",
  },
  tasks: {
    back: "Back to dashboard",
    title: "My Tasks",
    lastSync: "Last sync:",
    syncNow: "Sync now",
    syncing: "Syncing...",
    allSources: "All sources",
    allPriorities: "All priorities",
    priorityCritical: "Critical",
    priorityHigh: "High",
    priorityMedium: "Medium",
    priorityLow: "Low",
    allStatuses: "All statuses",
    statusOpen: "Open",
    statusInProgress: "In progress",
    statusCompleted: "Completed",
    statusOrphaned: "Orphaned",
    sortUpdated: "Recently updated",
    sortDue: "Due date",
    sortPriority: "Priority",
    sortTitle: "Title",
    loading: "Loading tasks...",
    noTasks: "No tasks yet",
    connectHint1: "Connect a PM system from",
    integrations: "Integrations",
    connectHint2: "and click Sync to pull in your assigned tasks.",
  },
  projects: {
    title: "Projects",
    noProjects: "No projects yet",
    count: "{{count}} projects",
    countOne: "1 project",
    showAll: "Show all",
    myProjects: "My projects",
    newProject: "New Project",
    searchPlaceholder: "Search projects…",
    sort: "Sort:",
    sortUpdated: "Updated",
    sortCreated: "Created",
    sortName: "Name",
    loadError: "Could not load projects",
    noMatch: "No projects match the current filters",
    clearFilters: "Clear filters",
    createFirst: "Create your first project",
  },
  dashboard: {
    failedToFetch: "Failed to fetch: {{status}}",
    failedToLoadPendingTasks: "Failed to load pending tasks",
    adminTitle: "Admin Dashboard",
    lastUpdated: "Last updated: {{time}}",
    welcome: "Welcome, {{email}}",
    panelCompany: "Company",
    totalUsers: "Total Users",
    activeUsers: "Active Users",
    byRole: "By Role:",
    panelProjects: "Projects",
    totalProjects: "Total Projects",
    active: "Active",
    panelTeamSummary: "Team Summary",
    total: "Total",
    panelFinancials: "Financials",
    revenue: "Revenue (This Month)",
    invoices: "Invoices",
    outstanding: "Outstanding",
    panelActivity: "Activity Feed",
    loadingActivities: "Loading activities...",
    noActivity: "No recent activity",
    panelPendingTasks: "Pending Tasks",
    loading: "Loading...",
    noActiveProjects: "No active projects.",
    colProject: "Project",
    colOpen: "Open",
    colInProgress: "In Prog",
  },
  settingsPage: {
    title: "Settings",
    subtitle: "Manage your company and account settings",
    tabCompany: "Company",
    tabProfile: "Profile",
    tabNotifications: "Notifications",
    tabSecurity: "Security",
    companyInfo: "Company Information",
    companyInfoDesc: "Update your company details and billing information",
    profileInfo: "Profile Information",
    profileInfoDesc: "Update your personal information and preferences",
    language: "Language",
    changePassword: "Change Password",
    notificationPrefs: "Notification Preferences",
    securitySettings: "Security Settings",
    saveChanges: "Save Changes",
    saveProfile: "Save Profile",
  },
  register: {
    title: "Welcome to wxKanban",
    settingUp: "Setting up your",
    account: "account",
    stepCompany: "Company",
    stepPayment: "Payment",
    stepAdmin: "Admin",
    successTitle: "Company Created Successfully!",
    verificationSent: "A verification email has been sent to",
    whatsNext: "What's next:",
    next1: "Check your email and click the verification link",
    next2: "Log in to your new wxKanban account",
    next3: "Complete your team setup and start managing projects",
    goToLogin: "Go to Login",
    createAnother: "Create Another Account",
    haveAccount: "Already have an account?",
    signIn: "Sign in",
    next: "Next",
    loading: "Loading...",
    back: "Back",
    companyTitle: "Company Information",
    companySubtitle: "Tell us about your company so we can set up your billing and communication",
    companyNameLabel: "Company Name *",
    billingEmailLabel: "Billing Email *",
    billingAddressLabel: "Billing Address *",
    countryLabel: "Country *",
    phoneLabel: "Phone Number *",
    referredByLabel: "Referred by (optional)",
    referredByHint: "If a distributor referred you, enter their code.",
    errCompanyNameRequired: "Company name is required",
    errCompanyNameMin: "Company name must be at least 2 characters",
    errCompanyNameMax: "Company name must be less than 255 characters",
    errBillingEmailRequired: "Billing email is required",
    errEmailInvalid: "Please enter a valid email address",
    errBillingAddressRequired: "Billing address is required",
    errBillingAddressMin: "Billing address must be at least 10 characters",
    errBillingAddressMax: "Billing address must be less than 500 characters",
    errCountryRequired: "Country is required",
    errCountryInvalid: "Please select a valid country code",
    errPhoneRequired: "Phone number is required",
    errPhoneMin: "Phone number must be at least 10 characters",
    errPhoneMax: "Phone number must be less than 20 characters",
    adminTitle: "Create Admin Account",
    adminSubtitle: "Set up your admin account to access wxKanban. This is the first user that will be created for your company.",
    emailLabel: "Email Address *",
    firstNameLabel: "First Name *",
    lastNameLabel: "Last Name (Optional)",
    passwordLabel: "Password *",
    pwReqTitle: "Password requirements:",
    pwReqLength: "At least 12 characters",
    pwReqUpper: "At least one uppercase letter",
    pwReqLower: "At least one lowercase letter",
    pwReqNumber: "At least one number",
    pwReqSpecial: "At least one special character (!@#$%^&*)",
    termsPrefix: "By creating an account, you agree to our",
    termsOfService: "Terms of Service",
    and: "and",
    privacyPolicy: "Privacy Policy",
    creatingAccount: "Creating Account...",
    completeRegistration: "Complete Registration",
    errEmailRequired: "Email is required",
    errPasswordRequired: "Password is required",
    errPasswordRequirements: "Password does not meet all requirements",
    errFirstNameRequired: "First name is required",
    errFirstNameLength: "First name must be 1-100 characters",
    errLastNameLength: "Last name must be 1-100 characters",
    paymentMethod: "Payment Method",
    preparingPayment: "Preparing secure payment…",
    initPaymentError: "Failed to initialize payment",
    subscribingTo: "You're subscribing to",
    at: "at",
    perMonth: "/month",
    stripeNotice: "Card details are handled directly by Stripe. Nothing sensitive touches our servers.",
    paymentFailedCard: "Payment failed. Please check your card and try again.",
    paymentFailed: "Payment failed",
    processing: "Processing…",
    completePayment: "Complete Payment",
    startPromo: "Start Free Period",
    inviteChecking: "Checking your invitation…",
    inviteInvalid: "This invitation link is not valid. It may have been used already, been revoked, or expired.",
    inviteFreeMonths: "You have been invited: your first {{days}} days on {{plan}} are free.",
    inviteBoundTo: "This invitation is for {{email}} — you will register with that address.",
    promoFreeForDays: "Free for your first {{days}} days.",
    promoFirstChargeOn: "Your first payment of {{amount}} is on {{date}}.",
    promoFirstChargeAfter: "Your first payment of {{amount}} is after {{days}} days.",
    promoCardNotChargedToday: "Your card will not be charged today. We keep it on file so your subscription continues automatically.",
    tierSingleUser: "Single User",
    tierSmallBusiness: "Small/Medium Business",
    tierConsultant: "Consultant",
    tierEnterprise: "Enterprise",
    tierFree: "Free",
    confTitle: "Confirm Your Registration",
    confSubtitle: "Please review your information before completing registration",
    confCompanyDesc: "Details about your organization",
    confCompanyName: "Company Name",
    confBillingEmail: "Billing Email",
    subscriptionPlan: "Subscription Plan",
    subscriptionPlanDesc: "Your selected tier and pricing",
    selectedPlan: "Selected Plan",
    monthlyPrice: "Monthly Price",
    adminAccount: "Admin Account",
    adminAccountDesc: "Your administrator login details",
    nameLabel: "Name",
    emailShort: "Email",
    termsClickPrefix: "By clicking \"Complete Registration\", you agree to our",
  },
  changePassword: {
    title: "Change Your Password",
    welcome: "Welcome, {{name}}! For security, please change your temporary password before continuing.",
    currentLabel: "Current Password (Temporary)",
    currentPlaceholder: "Enter your temporary password",
    newLabel: "New Password",
    newPlaceholder: "Create a new password (min 8 characters)",
    confirmLabel: "Confirm New Password",
    confirmPlaceholder: "Confirm your new password",
    submit: "Change Password & Continue",
    needHelp: "Need help? Contact your administrator or",
    supportTeam: "support team",
    errMinLength: "New password must be at least 8 characters",
    errMismatch: "Passwords do not match",
    errSameAsOld: "New password must be different from current password",
    errFailed: "Failed to change password",
    successRedirect: "Password changed successfully! Redirecting to dashboard...",
  },
  verification: {
    noToken: "No verification token provided. Please check your email link.",
    verifiedSuccess: "Your email has been verified successfully!",
    verifyFailed: "Failed to verify email. The link may have expired.",
    unexpectedError: "An unexpected error occurred. Please try again or contact support.",
    resendFailed: "Failed to resend verification email. Please try again.",
    networkError: "Network error. Please try again.",
    titleLoading: "Verifying your email...",
    titleSuccess: "Email verified!",
    titleError: "Verification failed",
    descLoading: "Please wait while we verify your email address",
    descSuccess: "You can now sign in to your account",
    descError: "We encountered an issue with your verification",
    signInNow: "Sign In Now",
    resending: "Resending...",
    resendButton: "Resend Verification Email",
    resentNotice: "Verification email resent! Check your inbox.",
    backToSignIn: "Back to Sign In",
    redirecting: "Redirecting to login in 3 seconds...",
  },
  invitePage: {
    errorNoToken: "No invitation token provided",
    errorInvalidExpired: "Invalid or expired invitation",
    errorPasswordMismatch: "Passwords do not match",
    errorPasswordTooShort: "Password must be at least 8 characters",
    errorAcceptFailed: "Failed to accept invitation",
    errorUnexpected: "An unexpected error occurred",
    invalidTitle: "Invalid Invitation",
    invalidDescription: "This invitation link is invalid or has expired.",
    invalidContactPrompt: "Please contact the person who invited you to request a new invitation.",
    goToSignIn: "Go to Sign In",
    acceptedTitle: "Welcome to the Team!",
    acceptedDescription: "Your account has been created successfully.",
    acceptedBody: "You can now sign in to access your company's wxKanban workspace.",
    signInNow: "Sign In Now",
    joinTitle: "Join {{companyName}}",
    invitedBy: "You've been invited by {{inviterName}} to join as a {{role}}",
    emailLabel: "Email",
    firstNameLabel: "First Name",
    lastNameLabel: "Last Name",
    passwordLabel: "Password",
    confirmPasswordLabel: "Confirm Password",
    creatingAccount: "Creating Account...",
    acceptAndCreate: "Accept Invitation & Create Account",
    alreadyHaveAccount: "Already have an account?",
    signInHere: "Sign in here",
  },
  inviteAccept: {
    subtitle: "Team Invitation",
    validating: "Validating invitation...",
    expiredTitle: "Invitation Expired",
    expiredBody: "This invitation link has expired. Ask your admin to resend it.",
    usedTitle: "Invitation Already Used",
    invalidTitle: "Invalid Invitation",
    usedBody: "This invitation has already been accepted. Try logging in.",
    invalidBody: "This invitation link is invalid or has expired.",
    goToLogin: "Go to Login",
    successTitle: "Account Created!",
    successBody: "Your account has been set up. You can now log in to wxKanban.",
    logIn: "Log In",
    joinCompany: "Join {{company}}",
    creatingAccountFor: "Creating account for",
    fullNameLabel: "Full name",
    passwordLabel: "Password",
    passwordPlaceholder: "Min. 8 characters",
    confirmPasswordLabel: "Confirm password",
    confirmPasswordPlaceholder: "Re-enter password",
    timezoneLabel: "Timezone",
    timezoneHint: "Auto-detected from your browser",
    creatingAccount: "Creating account...",
    createAccount: "Create Account",
    alreadyHaveAccount: "Already have an account?",
    signIn: "Sign in",
    errorPasswordMismatch: "Passwords do not match",
    errorPasswordTooShort: "Password must be at least 8 characters",
    errorCreateAccountFailed: "Failed to create account",
    errorUnknown: "Unknown error",
  },
  inviteTeam: {
    title: "Invite Team Members",
    subtitle: "Add colleagues to collaborate on your projects",
    sendInvitations: "Send Invitations",
    sendInvitationsDesc: "Choose how you want to invite your team members",
    singleInvite: "Single Invite",
    bulkUpload: "Bulk Upload",
    sendMore: "Send More Invitations",
    viewTeam: "View Team",
    tipsTitle: "Tips for Inviting Team Members",
    tipEmails: "Make sure to use the correct email addresses to avoid bounces",
    tipRoles: "Choose appropriate roles: Admin (full access), Manager (can manage projects), or Member (can view and complete tasks)",
    tipCsv: "For bulk uploads, use a CSV file with columns: email, usertype, firstName, lastName",
    tipExpiry: "Invitations expire after 7 days if not accepted",
  },
  teamDashboard: {
    title: "My Dashboard",
    welcomeBack: "Welcome back, {{email}}",
    assignedProjects: "Assigned Projects",
    openTasks: "Open Tasks",
    hoursThisWeek: "Hours This Week",
    noProjectsAssigned: "No projects assigned to you yet.",
    workHubTitle: "Consultant Work Hub",
    workHubDescription: "Open your unified task queue and track time directly on assigned work.",
    openWorkHub: "Open Work Hub",
  },
  distributorSignup: {
    errorPasswordsMismatch: "Passwords do not match",
    errorPasswordTooShort: "Password must be at least 8 characters",
    errorAcceptTerms: "Please accept the distributor terms",
    errorRegistrationFailed: "Registration failed",
    errorUnknown: "Unknown error",
    successHeading: "You're all set",
    successSignedInPrefix: "Your free distributor account is ready. You're signed in as",
    referralCodeLabel: "Your referral code",
    copyCodeTitle: "Copy code",
    referralCodeHelp: "Share this code with the people you refer. When they enter it while signing up, their account is credited to you.",
    goToDashboard: "Go to your dashboard",
    introBannerBefore: "Become a wxKanban distributor. Set up your",
    introBannerFree: "free",
    introBannerAfter: "account below — you'll get a referral code to share, and you'll be signed in automatically when done.",
    fieldBusinessName: "Business name *",
    fieldFullName: "Your full name *",
    fieldEmail: "Email *",
    fieldPassword: "Password *",
    placeholderPassword: "At least 8 characters",
    fieldConfirmPassword: "Confirm password *",
    placeholderConfirmPassword: "Re-enter password",
    acceptTermsLabel: "I accept the distributor terms. My account is a free single-user account — I'm not subscribed to a paid plan.",
    creatingAccount: "Creating account…",
    createAccountButton: "Create free account",
    pageSubtitle: "Distributor Sign-up",
  },
  overviewPage: {
    loadFailed: "Failed to load overview document ({{status}})",
    unknownError: "Unknown error",
    dashboard: "Dashboard",
    projectOverview: "Project Overview",
    refresh: "Refresh",
    loadErrorTitle: "Could not load overview document",
  },
  projectWorkspace: {
    // [SCOPE 137 / T019] Change Impact mode (owner-set)
    impactModeTitle: "Change Impact mode",
    impactModeHelp: "How the kit's Change Impact check treats AI edits in this project. It applies to every developer's kit and overrides a value set locally.",
    impactModeNotify: "Notify: tell the developer what an edit affects, never ask",
    impactModeBoundaries: "Boundaries: ask only when an edit reaches another scope or a signed-off test (default)",
    impactModeAlways: "Always: ask whenever an edit affects anything else",
    impactModeNotSet: "Not set here. Each developer's kit uses its local value, or Boundaries.",
    impactModeSetBy: "Set by {{who}} on {{when}}",
    impactModeOwnerOnly: "Only the project owner or a company administrator can change this.",
    impactModeDataLoss: "Possible data loss is stopped in every mode.",
    impactModeError: "The change was not saved. Please try again.",
    complianceTabSettings: "Settings",
    complianceTabDocuments: "Documents",
    complianceTabTasks: "Tasks",
    complianceTabAudit: "Audit",
    complianceTabReports: "Reports",
    learningsGenerateFailed: "Failed ({{status}})",
    learningsPrintHint: "Use browser print to save as PDF",
    regenerate: "Regenerate",
    learningsIframeTitle: "Project Learnings",
    learningsNoneTitle: "No learnings report yet",
    learningsRunPrefix: "Run",
    learningsRunSuffix: "after completing work to capture decisions, fixes, and compliance controls.",
    learningsThenGenerate: "Then generate the report below.",
    generating: "Generating…",
    generateLearningsReport: "Generate Learnings Report",
    errFailedLoadProject: "Failed to load project",
    confirmArchive: "Archive this project? It will be hidden from the project list.",
    confirmDelete: "Permanently delete \"{{name}}\"? This removes the project and all of its tasks, phases, specs, and documents. This cannot be undone.",
    thisProject: "this project",
    errFailedDeleteProjectStatus: "Failed to delete project ({{status}})",
    errFailedDeleteProject: "Failed to delete project",
    errFailedUpdateProject: "Failed to update project",
    errFailedUpdateAuditType: "Failed to update audit type",
    loadingProject: "Loading project...",
    projectNotFound: "Project not found",
    backToProjects: "Back to Projects",
    projects: "Projects",
    clientEngagementTitle: "Accessed via active engagement with {{name}}",
    client: "Client",
    clientProjectBadge: "Client project — {{name}}",
    editProject: "Edit Project",
    auditLabel: "Audit:",
    noAudit: "No Audit",
    archiveProject: "Archive project",
    archive: "Archive",
    deleteProjectTitle: "Permanently delete project",
    delete: "Delete",
    tabLifecycle: "Lifecycle",
    tabDevelopmentPlan: "Development Plan",
    tabPortfolioAnalyzer: "Portfolio Analyzer",
    tabCompliance: "Compliance",
    tabLearnings: "Learnings",
    tabKit: "Kit",
    views: "Views",
    viewMatrix: "Matrix",
    viewStack: "Stack",
    viewTheme: "Theme",
    viewBugReports: "Bug Reports",
    iframeLifecycle: "Project Lifecycle",
    iframeDevplan: "Development Plan",
    phaseNotStarted: "This phase hasn't started yet.",
    saveFailed: "Save Failed",
    saveErrorIntro: "The following error occurred while saving:",
    copyToClipboard: "Copy to clipboard",
    copy: "Copy",
    copyHint: "Click \"Copy\" to copy this error message to your clipboard for reporting.",
    close: "Close",
    copyAndClose: "Copy & Close",
    fieldProjectName: "Project Name",
    fieldDescription: "Description",
    fieldHourlyRate: "Hourly Rate ($)",
    fieldAllocatedHours: "Allocated Hours",
    fieldPmSystems: "PM Systems",
    fieldSecurityClassification: "Security Classification",
    fieldAuditType: "Audit Type",
    phaseAssignments: "Phase Assignments",
    loadingTeamMembers: "Loading team members...",
    unassigned: "Unassigned",
    reassignPermissionNote: "Only ADMIN and TEAM_MANAGER can reassign phases.",
    cancel: "Cancel",
    saving: "Saving...",
    saveChanges: "Save Changes",
  },
  helpPage: {
    backToHelpCenter: "Back to Help Center",
    examples: "Examples",
    wasArticleHelpful: "Was this article helpful?",
    yesItHelped: "Yes, it helped",
    noNotHelpful: "No, not helpful",
    helpCenter: "Help Center",
    // [SCOPE 138 / T011] "Learn wxKanban with your AI"
    teachTitle: "Learn wxKanban with your AI",
    teachHowTo: "Download the lessons, give start-here.md (or the whole zip) to any AI chat, and say \"teach me\". Each lesson walks you through one task, step by step.",
    teachDownload: "Download the lessons",
    teachVersion: "wxKanban {{version}} · {{count}} lessons",
    teachError: "The download failed. Please try again.",
    subtitle: "Find answers, documentation, and guides for wxKanban",
    searchPlaceholder: "Search for help articles, CLI commands, or guides...",
    searchResults: "Search Results",
    searching: "Searching...",
    noResults: "No results found for \"{{query}}\"",
    viewsCount: "{{count}} views",
    topicsCount: "{{count}} topics",
    moreCount: "+{{count}} more",
  },
  planHelp: {
    title: "Help",
    subtitlePrefix: "What every wxKanban area does, and what your plan includes. Showing",
    subtitleSuffix: "by default — pick another plan to compare.",
    yourPlan: "your plan",
    included: "Included",
    upgradeRequired: "Upgrade required",
    loading: "Loading your plan…",
  },
  faqPage: {
    title: "Help & FAQ",
    subtitle: "Browse answers, or ask a question and we'll add it to the FAQ.",
    panel: {
      heading: "Frequently asked questions",
      loading: "Loading…",
      empty: "No FAQs yet — ask a question below.",
      askPrompt: "Didn't find your answer? Ask us",
      placeholder: "Type your question…",
      submit: "Submit question",
      sending: "Sending…",
      tooShort: "Please enter a question (at least 5 characters).",
      submitError: "Could not submit your question.",
      thanks: "Thanks — we'll answer this.",
    },
  },
  aboutPage: {
    title: "About wxKanban",
    lede: "wxKanban is a spec-driven way to build software with AI — it teaches you to work the way professionals do, taking an application across the full lifecycle from a messy idea to a clean, tested, shipped result, on foundations you actually own.",
    whatItIsHeading: "What it is",
    whatItIsPara1: "wxKanban is a spec-driven development environment. You work through a guided six-phase lifecycle — Design → Implementation → QA → Human Testing → Beta → Release — where nothing gets built without an approved spec, and every change is traceable back to the spec that authorized it. Just as importantly, it keeps your specs and documentation consistent as the software evolves: future changes are checked against what's already written, so your documentation updates with the work instead of drifting out of date.",
    whatItIsIntro: "Around that lifecycle, wxKanban gives you what you need to run real work as a team:",
    teamMgmtLabel: "Team management",
    teamMgmtBody: "— invite people, set their roles, and control who can do what.",
    checkoutLabel: "Check-out & check-in",
    checkoutBody: "— claim a scope to work on it and check it back in when you're done, so work stays coordinated and no one quietly overwrites someone else.",
    cockpitLabel: "The Cockpit",
    cockpitBody: "— a live command center for your project: where it stands, what's next, and every wxKanban command in one place.",
    helpLabel: "Active, in-context help",
    helpBody: "— guidance and answers right where you work, both inside wxKanban and inside VS Code, where the building actually happens.",
    whatItIsClosing: "That's the heart of wxKanban: the spec-driven lifecycle, with the tools to run it as a team.",
    whyHeading: "Why it exists",
    whyPara1: "AI has changed how software gets built, but the hard part was never the syntax — it was the ",
    whyEmphasis: "judgment",
    whyPara2: " to take an application all the way to shipped. wxKanban is built to hand experienced developers a way to carry that judgment into the AI era, and to give newcomers that discipline from day one — so AI accelerates your work instead of running away with it.",
    groundHeading: "Built on ground you own",
    groundPara: "Your data lives in a standard SQL database, your authentication is your own, and you can swap AI providers whenever you like. No proprietary lock-in, no vendor standing between you and the software you depend on.",
    behindHeading: "Who's behind it",
    behindPara1: "wxKanban is made by ",
    behindPara2: ", founded by Andy “Cowboy” Stapleton — who has spent 40+ years crossing every major platform shift, from mainframe to web to cloud, and helping other developers make the same crossing.",
    contactHeading: "Get in touch",
    contactIntro: "Questions or ideas? Email",
    contactOutro: "— we actually answer.",
    phoneLabel: "Phone:",
  },
  bioPage: {
    title: "About the founder",
    photoAlt: "Portrait of the founder",
    loading: "Loading…",
  },
  privacyPage: {
    title: "Privacy Policy",
    lastUpdated: "Last updated: {{date}}",
    intro:
      "wxKanban (“we,” “wxperts”) respects your privacy. This policy explains what we collect when you use the wxKanban application, why, and the choices you have. We collect what we need to run the service, we don't sell your data, and you can ask us to delete it.",
    authoritativeNote:
      "This policy has been translated for convenience. The English version is the authoritative version.",
    sections: {
      s1heading: "Information we collect",
      s1item1:
        "Account & company details — your name, email address, password (stored only as a secure hash), company name, role, and language preference.",
      s1item2:
        "The work you create — projects, tasks, specifications, notes, time entries, invoices, and related content you enter into the app.",
      s1item3:
        "Billing information — subscription and payment details are processed by our payment provider, Stripe. We do not store full card numbers; we keep your plan, status, and billing history.",
      s1item4:
        "Connected services — if you link a project-management tool (e.g., Jira, Monday.com, Asana, Trello), we store the access tokens needed to sync, encrypted at rest.",
      s1item5:
        "Usage & logs — basic technical logs (such as actions, timestamps, and errors) used to operate, secure, and improve the service.",
      s2heading: "How we use it",
      s2item1: "To provide and operate the app — your account, projects, time tracking, and invoicing.",
      s2item2: "To process subscriptions and payments through Stripe.",
      s2item3: "To sync with the project-management tools you choose to connect.",
      s2item4: "To power AI-assisted features (see below).",
      s2item5: "To secure the service, prevent abuse, and provide support.",
      s3heading: "AI processing",
      s3body:
        "Some features send content you provide (for example, specifications or related text) to third-party AI providers — such as Google Gemini, OpenAI, or others we configure — solely to generate the requested result. We don't use your content to train our own models, and AI providers process it under their own terms as our service providers.",
      s4heading: "Who we share it with",
      s4body:
        "We don't sell or rent your information. We share it only with the service providers that help us run wxKanban — for example, cloud hosting (Amazon Web Services), payment processing (Stripe), and the AI providers above — and only so they can perform those services for us. We may disclose information if required by law.",
      s5heading: "Security",
      s5body:
        "We protect your data with encryption in transit and at rest, hashed passwords, and access controls. No system is perfectly secure, but we work continuously to safeguard your information.",
      s6heading: "How long we keep it",
      s6body:
        "We keep your account and the content you create for as long as your account is active, and for a reasonable period afterward as needed for legal, accounting, and operational purposes. You can ask us to delete your data at any time.",
      s7heading: "Your choices and rights",
      s7body:
        "You can access, correct, export, or delete your personal information, and withdraw consent where it applies. If you're in a region with specific data-protection rights (such as the EU or UK under the GDPR, or U.S. state privacy laws), those rights apply to you. To exercise them, email",
      s8heading: "International transfers",
      s8body:
        "wxKanban is operated from the United States, and your information may be processed there. By using the service, you consent to that transfer, subject to the protections described in this policy.",
      s9heading: "Contact",
      s9body: "Questions about privacy? Email",
    },
  },
  termsPage: {
    title: "Terms of Use",
    lastUpdated: "Last updated: {{date}}",
    intro:
      "These terms govern your use of the wxKanban application and website. By creating an account or using the service, you agree to them. Please also read our",
    authoritativeNote:
      "These terms have been translated for convenience. The English version is the authoritative version.",
    sections: {
      s1heading: "Your account",
      s1body:
        "You're responsible for the accuracy of your account details and for keeping your credentials secure. You're responsible for activity that happens under your account, and for the team members you invite.",
      s2heading: "Acceptable use",
      s2body:
        "Use wxKanban lawfully. Don't attempt to disrupt or gain unauthorized access to the service, misuse other users' data, or use the platform to build or store unlawful content.",
      s3heading: "Subscriptions & billing",
      s3body:
        "Paid plans are billed through Stripe on a recurring basis until cancelled. Fees are charged in advance for each billing period and are non-refundable except where required by law. You can cancel at any time; your plan remains active through the end of the current period. We'll give reasonable notice of price changes.",
      s4heading: "Your content & ownership",
      s4body:
        "You own the data and content you create in wxKanban. You grant us the limited license needed to host, process, back up, and display it so we can provide the service. You can export or delete your content as described in our Privacy Policy.",
      s5heading: "AI-generated output",
      s5body:
        "wxKanban uses AI to assist your work. AI output can be imperfect — you are responsible for reviewing, testing, and approving anything generated before relying on it. We don't warrant that AI-assisted results are accurate, complete, or fit for a particular purpose.",
      s6heading: "Our intellectual property",
      s6body:
        "The wxKanban application, its design, and the wxKanban and wxperts names belong to wxperts or its licensors. These terms don't grant you any right to our software or branding beyond using the service as intended.",
      s7heading: "No warranties",
      s7body:
        "The service is provided “as is” and “as available,” without warranties of any kind. We work to keep wxKanban reliable and available but do not guarantee uninterrupted or error-free operation.",
      s8heading: "Limitation of liability",
      s8body:
        "To the fullest extent permitted by law, wxperts is not liable for indirect, incidental, or consequential losses, or for loss of data or profits, arising from your use of — or inability to use — the service.",
      s9heading: "Termination",
      s9body:
        "You may close your account at any time. We may suspend or terminate access if these terms are breached or to protect the service. On termination, the provisions that by their nature should survive will continue to apply.",
      s10heading: "Governing law",
      s10body:
        "wxperts operates from the United States, and these terms are governed by U.S. law, without regard to conflict-of-laws principles. If you use the service from the European Union, nothing in these terms removes the mandatory consumer- and data-protection rights you have under EU law (including the GDPR); those protections continue to apply to you.",
      s11heading: "Changes",
      s11body:
        "We may update these terms as the product evolves. The “last updated” date above shows when they last changed; continued use after a change means you accept it.",
      s12heading: "Contact",
      s12body: "Questions about these terms? Email",
    },
  },
  adminDashboard: {
    saveFailed: "Save failed",
    editAuditType: "Edit Audit Type",
    complianceFramework: "Compliance Framework",
    none: "— None —",
    title: "Admin Dashboard",
    welcomeBack: "Welcome back, {{email}}",
    teamMembers: "Team Members",
    activeProjects: "Active Projects",
    hoursThisWeek: "Hours This Week",
    timeBilling: "Time & Billing",
    timeBillingDesc: "Track time, set billing rates, and manage billable hours across all projects.",
    timeBillingPlaceholder: "Time entries and billing rates will appear here.",
    systemHealth: "System Health",
    reportsAnalytics: "Reports & Analytics",
    reportsDesc: "Revenue summaries, hours by project, profitability, and exportable reports.",
    reports: "Reports",
    reportsPlaceholder: "Financial reports will appear here once time entries are recorded.",
  },
  subscriptionCard: {
    statusActive: "Active",
    statusPending: "Pending",
    statusSuspended: "Suspended",
    currentPlan: "Current Plan",
    teamSeats: "Team seats",
    seatLimitReached: "Seat limit reached — upgrade to invite more members.",
    upgradePlan: "Upgrade Plan",
  },
  // [SCOPE 131 / T013] Suspend & cancel copy (FR-011, FR-009).
  // Authored so the difference between suspending and cancelling is legible
  // without reading help: suspend says "nothing is deleted", cancel says what
  // date access ends and that it can be undone until then.
  subscriptionLifecycle: {
    sectionTitle: "Suspend or cancel",
    sectionDesc:
      "Pause billing for a while, or cancel your subscription at the end of the period you have already paid for.",
    stateActive: "Your subscription is active.",
    statePaused: "Billing is suspended.",
    statePausedDetail:
      "No invoices are being issued and no data has been deleted. Resume whenever you are ready — your projects and history are waiting.",
    stateCancelPending: "Cancellation scheduled.",
    stateCancelPendingDetail:
      "You keep full access until {{date}}. You can reverse this at any time before then.",
    effectiveDateUnknown: "the end of your current billing period",
    pauseTitle: "Suspend billing",
    pauseDesc:
      "Stripe stops invoicing you and your team loses access until you resume. Nothing is deleted and nothing is scheduled for deletion.",
    pauseAction: "Suspend billing",
    unpauseAction: "Resume billing",
    cancelTitle: "Cancel subscription",
    cancelDesc:
      "Your subscription ends when the period you have already paid for runs out. You keep full access until then.",
    cancelAction: "Cancel subscription",
    undoCancelAction: "Keep my subscription",
    pauseSuccess: "Billing suspended. Resume whenever you are ready.",
    unpauseSuccess: "Billing resumed — welcome back.",
    cancelSuccess: "Your subscription will end on {{date}}. You can reverse this until then.",
    undoCancelSuccess: "Cancellation reversed. Your subscription continues as normal.",
    actionFailed: "That did not go through, and nothing was changed. Please try again.",
    // Typed-confirmation dialog (T011)
    dialogTitle: "Cancel your subscription?",
    dialogBody:
      "Your subscription will stay active until {{date}}. After that your team loses access, and 30 days later your company data is archived.",
    dialogUndoNote: "You can reverse this at any time before {{date}}.",
    dialogTypeLabel: "Type {{name}} to confirm",
    dialogPlaceholder: "Company name",
    dialogConfirm: "Cancel subscription",
    dialogKeep: "Keep my subscription",
    dialogMismatch: "That does not match your company name.",
    // Wall copy for a self-suspended company (T012)
    wallPausedTitle: "Billing is suspended",
    wallPausedBody:
      "Your company suspended its own billing, so the product is paused. Nothing has been deleted. A company admin can resume it from Settings → Billing & Subscription.",
    wallPausedCta: "Go to billing settings",
    wallPausedNonAdmin:
      "Your company suspended its billing. Nothing has been deleted — ask your company admin to resume it.",
    // Cancellation confirmation email (T014)
    emailSubject: "Your wxKanban subscription will end on {{date}}",
    emailHeading: "Your cancellation is confirmed",
    emailBody:
      "We have scheduled your wxKanban subscription to end on {{date}}. You keep full access to {{company}} until that date, and you will not be charged again.",
    emailUndoHeading: "Changed your mind?",
    emailUndoBody:
      "You can reverse this at any time before {{date}} from Settings → Billing & Subscription. Nothing is deleted while your cancellation is still pending.",
    emailFooter: "If you did not request this, contact your company admin straight away.",
  },
  engagementsWidget: {
    title: "Engagements",
    clientFirms: "{{count}} consulting firm on your projects",
    clientFirmsPlural: "{{count}} consulting firms on your projects",
    clientsServed: "{{count}} client you serve",
    clientsServedPlural: "{{count}} clients you serve",
    viewAll: "View all",
    roleHiring: "Hiring",
    roleServing: "Serving",
    moreCount: "+ {{count}} more",
  },
  activityFeed: {
    title: "Activity Feed",
    loading: "Loading recent activity...",
    subtitle: "Recent team and project activity",
    viewAll: "View All",
    empty: "No recent activity",
    emptyHint: "Activity will appear here as your team works",
    byUser: "by {{name}}",
    justNow: "just now",
    minutesAgo: "{{count}}m ago",
    hoursAgo: "{{count}}h ago",
    daysAgo: "{{count}}d ago",
  },
  dailyDigest: {
    title: "Daily Digest",
    loadingActivity: "Loading today's activity...",
    subtitle: "Today's team activity summary",
    configure: "Configure",
    viewAll: "View All",
    noActivity: "No activity yet today",
    activityWillAppear: "Team activity will appear here",
    byUser: "by {{name}}",
    moreItems: "+{{count}} more items",
    justNow: "just now",
    minutesAgo: "{{count}}m ago",
    hoursAgo: "{{count}}h ago",
  },
  roleLock: {
    contactAdmin: "Contact your Administrator to access this section.",
    requiresAdmin: "Requires Admin",
  },
  upgradeLock: {
    requiresPlan: "Requires {{plan}} plan",
    upgradePlan: "Upgrade Plan",
  },
  projectProposals: {
    errorFindDraft: "Failed to find overview draft for this proposal",
    errorOpenEditor: "Failed to open editor",
    errorFetch: "Failed to fetch proposals: {{status}}",
    errorLoad: "Failed to load proposals",
    errorUpdateStatus: "Failed to update status",
    rejectionReasonPrompt: "Rejection reason (required):",
    title: "Project Proposals",
    addProposal: "Add Proposal",
    loadingProposals: "Loading proposals...",
    empty: "No proposals yet. Add one to get started.",
    status_pending: "Pending",
    status_good_idea: "Good Idea",
    status_accepted: "Accepted",
    status_rejected: "Rejected",
    editTitle: "Open the overview editor for this proposal",
    goodIdea: "Good Idea",
    reject: "Reject",
    accept: "Accept",
    createProject: "Create Project",
    reopen: "Reopen",
    projectCreated: "Project created",
  },
  systemHealth: {
    title: "System Health",
    checked: "Checked {{time}}",
    refresh: "Refresh",
    runningDiagnostics: "Running diagnostics…",
    allSystemsOperational: "All systems operational",
    driftOrMissingDetected: "Schema drift or missing tables detected",
    databaseUnreachable: "Database unreachable",
    database: "Database",
    status: "Status",
    latency: "Latency",
    errorLabel: "Error",
    tableRows: "{{table}} rows",
    server: "Server",
    uptime: "Uptime",
    memory: "Memory",
    env: "Env",
    diagTime: "Diag time",
    pmIntegrationsConnected: "PM integrations connected",
    pmIntegrationConnected: "PM integration connected",
    schemaDriftTables: "Schema Drift — {{count}} tables",
    schemaDriftTable: "Schema Drift — {{count}} table",
    missing: "missing",
    run: "Run",
    toFix: "to fix.",
    missingTables: "Missing Tables ({{count}})",
    toCreateMissingTables: "to create missing tables.",
  },
  projectGrid: {
    opening: "Opening…",
    lastUpdated: "Last updated: {{date}}",
    notCreated: "not created",
    noAudit: "No Audit",
    editAuditType: "Edit audit type",
    outstandingTasks: "Outstanding tasks",
    complete: "Complete",
    inProgress: "In Progress",
    taskProgress: "{{total}} tasks - {{completed}} completed",
    notStarted: "Not Started",
    failedOpenOverview: "Failed to open overview editor",
    failedOpenOverviewStatus: "Failed to open overview editor ({{status}})",
    titleProjectOverview: "Project Overview",
    titleProjectLifecycle: "Project Lifecycle",
    titleDevelopmentPlan: "Development Plan",
    titleAuditReport: "Audit Report",
    titleCompoundLearnings: "Compound Learnings",
    titleProjectScopeAnalysis: "Project Scope Analysis",
    titleEntityDiagram: "Entity Diagram",
    showDocuments: "Show documents",
    hideDocuments: "Hide documents",
    newProject: "New",
    allProjects: "All projects",
    active: "Active",
    branched: "Branched",
    emptyAdmin: "No active projects yet. Click New to create your first project.",
    emptyTeam: "No projects assigned to you yet.",
  },
  phaseTabBar: {
    assignedUser: "Assigned user",
  },
  designPhase: {
    specifications: "Specifications",
    clarifiedCount: "{{clarified}} / {{total}} clarified",
  },
  implementationPhase: {
    tasksHeading: "Tasks",
    openCount: "{{n}} open",
  },
  regression: {
    loading: "Comparing against previous runs…",
    failed: "Could not build the comparison",
    title: "Regression check for {{version}}",
    blocking: "{{count}} blocking",
    newlyFailing: "Started failing",
    newlyFailingHint: "Passed the last time it ran. This is what stops the release.",
    neverPassed: "Never passed",
    neverPassedHint: "Outstanding work, not a regression.",
    stillPassing: "Still passing",
    lastPassed: "Last passed in {{version}}",
  },
  testSuite: {
    skipTitle: "Testing requirement",
    skipOn: "Testing is OFF for this project.",
    skipOff: "Testing is required for this project.",
    skipTurnOn: "Skip testing",
    skipReEnable: "Require testing again",
    skipCancel: "Cancel",
    skipConfirm: "This switches the release gate off for EVERY scope in this project. Forced tests will stop blocking, and releases will be marked as skipped rather than tested. This is recorded against your name.",
    skipConfirmYes: "Yes, skip testing",
    skipHint: "Administrators only. The change is written to the audit log.",
    skipBannerTitle: "Testing is switched off for this project",
    skipBannerBody: "Forced tests are not blocking releases. Gates report as skipped, not passed.",
    scopeFilter: "Scope:",
    allScopes: "All scopes ({{count}})",
    loading: "Loading test suite…",
    loadFailed: "Could not load the test suite",
    runFailed: "Could not start the run",
    empty: "No test items yet. They are generated when the scope is implemented.",
    cycle: "Version {{version}}",
    cycleStatus: { open: "In build", uat: "In UAT", production: "In production", superseded: "Superseded" },
    humanPending: "{{count}} user test(s) waiting for release to UAT",
    runGate: "Run {{gate}}",
    noItemsForGate: "No items in the {{gate}} gate",
    runRunning: "Run in progress",
    runStalled: "Run stalled — no report from the runner",
    runTotals: "{{passed}} passed · {{failed}} failed · {{total}} total",
    required: "Required",
    origin: { ai: "AI", user: "Tester" },
    inherit: "Inherits", override: "Override",
    forceInherited: "Inherits the scope's force-test setting",
    forceOverridden: "Overrides the scope's force-test setting",
    forceScopeTitle: "Force scope test",
    forceScopeLabel: "Require signoff for all {{count}} tests in this scope",
    forceScopeHint: "When on, the product cannot move toward production until these tests are signed off.",
    filter: { all: "All", outstanding: "Outstanding", forced: "Required", failing: "Failing" },
  },
  userTest: {
    loading: "Loading your tests…",
    loadFailed: "Could not load your tests",
    signoffFailed: "Could not record that",
    dismissFailed: "Could not dismiss the retest",
    notReleasedTitle: "Not ready for you yet",
    notReleasedBody: "{{count}} test(s) are waiting. They are released once the automated tests pass for this version.",
    summary: "{{required}} required · {{optional}} optional · version {{version}}",
    required: "Required", optional: "Optional",
    testerAuthored: "Added by a tester",
    addTest: "Add a test",
    preconditions: "Before you start", steps: "Steps", postconditions: "Check afterwards",
    action: "Do this", expected: "You should see",
    pass: "Pass", fail: "Fail",
    notesPlaceholder: "What happened? (required if it failed)",
    failNeedsReason: "Say what actually happened — it becomes the bug report.",
    failHint: "A failure files a bug report with these steps attached.",
    bugFiled: "Bug report filed with your steps and what you saw.",
    viewBug: "Open the bug report",
    retestTitle: "Please test this again",
    retestImpact: "Code this test covers changed: {{path}} ({{hops}} step(s) away).",
    retestBroad: "A wide-reaching change landed, so every user test is being re-checked.",
    retestUndetermined: "We could not tell whether the change affects this test, so we are asking.",
    dismissRetest: "Not affected",
    dismissExplain: "If the change could not have affected what you verified, say why. Your earlier signoff stands.",
    dismissPlaceholder: "Why this change cannot affect this test",
    confirmDismiss: "Dismiss retest",
  },
  addTest: {
    title: "Add a test from what you found",
    intro: "You found something the specification did not anticipate. Write it down so it is checked every release.",
    whatHappened: "What should be true",
    titlePlaceholder: "e.g. Changing phase while a timer runs keeps the duration correct",
    setup: "Set-up (one per line)",
    setupPlaceholder: "e.g. A project with a running timer",
    steps: "Steps",
    actionPlaceholder: "Do this…",
    expectedPlaceholder: "You should see… (a value, a count, a message)",
    addStep: "Add a step",
    after: "Check afterwards (one per line)",
    afterPlaceholder: "e.g. The time entry total is unchanged",
    vagueWarning: "Step(s) {{steps}} say something like \"works\". Say what you should actually see — a value, a count, or a message.",
    needTitle: "Give the test a title",
    needStep: "Add at least one step",
    needExpected: "Every step needs an expected result",
    failed: "Could not save the test",
    cancel: "Cancel", save: "Save test",
  },
  qaPhase: {
    testTasks: "Test Tasks",
    passCount: "{{n}} pass",
    failCount: "{{n}} fail",
  },
  humanTestingPhase: {
    feedbackItems: "Feedback Items",
    issuesRaisedByTesters: "Issues raised by human testers",
    openCount: "{{count}} open",
  },
  betaPhase: {
    approvalTitle: "Beta Phase — Admin Approval Required",
    approvalBody: "Creating new tasks during the Beta phase requires explicit admin approval. This will be logged in the audit trail.",
    approveAndCreate: "Approve & Create",
    feedbackTitle: "Beta Feedback",
    feedbackHint: "Modifications require admin approval",
    openCount: "{{count}} open",
    creatingApprovedTask: "Creating approved task...",
  },
  releasePhase: {
    statusComplete: "Complete",
    statusInProgress: "In Progress",
    statusReopened: "Reopened",
    statusPending: "Pending",
    manualDeployment: "Manual Deployment",
    ciCdPipeline: "CI/CD Pipeline",
    stagedRollout: "Staged Rollout",
    hotfix: "Hotfix",
    releaseRecord: "Release Record",
    priorPhasesIncomplete: "Prior phases incomplete",
    versionNumber: "Version Number",
    versionPlaceholder: "e.g. v1.0.0",
    releaseDate: "Release Date",
    releaseMethod: "Release Method",
    releaseNotes: "Release Notes",
    notesPlaceholder: "Summarise what was released, key changes, known issues...",
    saved: "Saved",
    saveNotes: "Save Notes",
    fullLifecycleSummary: "Full Lifecycle Summary",
    columnPhase: "Phase",
    columnStatus: "Status",
    columnEntered: "Entered",
    columnCompleted: "Completed",
    projectLabel: "Project",
  },
  phaseCompletionBar: {
    advanceFailed: "Failed to advance phase",
    reopenFailed: "Failed to reopen phase",
    checkingCriteria: "Checking completion criteria...",
    phaseComplete: "Phase Complete",
    phaseReopened: "Phase Reopened",
    readyToAdvance: "Ready to advance",
    criteriaMet: "{{pass}} of {{total}} criteria met",
    collapse: "Collapse",
    expand: "Expand",
    advancing: "Advancing...",
    markAsReleased: "Mark as Released",
    advancePhase: "Advance Phase",
    reopenedForRevisions: "This phase has been reopened for revisions.",
    cancelReopen: "Cancel reopen",
    reopenPhase: "Reopen phase",
    reopenReasonPlaceholder: "Reason for reopening this phase...",
    reopening: "Reopening...",
    confirmReopen: "Confirm Reopen",
  },
  phaseTaskGroup: {
    unassigned: "Unassigned",
    unnamedScope: "Unnamed Scope",
    noTasksInScope: "No tasks in this scope.",
    noTasksInPhase: "No tasks in this phase.",
    unscopedTasks: "Unscoped tasks",
    failedToLoad: "Failed to load tasks",
    requestFailed: "Request failed: {{status}}",
  },
  taskList: {
    statusOpen: "Open",
    statusInProgress: "In Progress",
    statusCompleted: "Completed",
    statusDismissed: "Dismissed",
    resultNone: "—",
    resultPass: "Pass",
    resultFail: "Fail",
    resultBlocked: "Blocked",
    priorityLow: "Low",
    priorityMed: "Med",
    priorityHigh: "High",
    priorityCrit: "Crit",
    taskNamePlaceholder: "Task name...",
    descriptionPlaceholder: "Description (optional)...",
    addTaskButton: "Add Task",
    addTask: "Add task",
    createError: "Failed to create task",
    noTasks: "No tasks yet",
    colTask: "Task",
    colResult: "Result",
    colRaisedBy: "Raised by",
    colPriority: "Priority",
    colStatus: "Status",
    countOpen: "{{n}} open",
    countClosed: "{{n}} closed",
    countTotal: "{{n}} total",
  },
  phase: {
    design: "Design",
    implementation: "Implementation",
    qa: "QA",
    humanTesting: "Human Testing",
    beta: "Beta",
    release: "Release",
  },
  projectPanel: {
    saveFailed: "Save failed",
    failedToLoad: "Failed to load",
    loadingTasks: "Loading tasks…",
    noTasksForScope: "No tasks for this scope.",
    filterAll: "all",
    filterOpen: "open",
    filterClosed: "closed",
    noFilterTasks: "No {{filter}} tasks.",
    openTasks: "Open Tasks",
    noOpenTasks: "No open tasks.",
    statTotal: "Total",
    statOpen: "Open",
    statClosed: "Closed",
    statInProgress: "In Progress",
    statComplete: "Complete",
    statActive: "Active",
    statResolved: "Resolved",
    statScopesPassing: "Scopes Passing",
    statQaTasksDone: "QA Tasks Done",
    statNoTasks: "No Tasks",
    documents: "Documents",
    completeCheck: "✓ complete",
    tasksCount: "Tasks ({{done}}/{{total}})",
    specOpenClosed: "Spec {{num}}: Open {{open}} · Closed {{closed}}",
    // [SCOPE 137 / T014] Change Impact agreement log (read-only)
    changeImpactTitle: "Change Impact log",
    changeImpactCount: "Change Impact log ({{count}})",
    changeImpactLoading: "Loading…",
    changeImpactEmpty: "No agreements or crossings have been recorded for this scope.",
    changeImpactError: "The Change Impact log could not be loaded.",
    changeImpactKindAck: "Acknowledged",
    changeImpactKindApproval: "Approved",
    changeImpactKindDeclined: "Declined",
    changeImpactKindCrossing: "Crossed, unattended",
    changeImpactAffected: "{{count}} affected",
    changeImpactQueued: "sent after an offline period",
    changeImpactReadOnly: "Read-only. These records cannot be changed.",
    reopen: "Reopen",
    reactivate: "Reactivate",
    markClarified: "Mark Clarified",
    defer: "Defer",
    needsClarification: "Needs Clarification",
    deleteScope: "Delete",
    deleteScopeConfirm: "Permanently delete scope {{num}} \"{{title}}\" and its {{tasks}} task(s) and {{docs}} document(s)? This cannot be undone.",
    deleteScopeError: "Could not delete the scope. Please try again.",
    overviewDoc: "Overview doc",
    scopeCountOne: "{{n}} scope",
    scopeCountOther: "{{n}} scopes",
    fullyGenerated: "{{n}} fully generated",
    noScopesCreatedYet: "No scopes created yet.",
    scopeDocuments: "Scope Documents",
    noScopes: "No scopes.",
    progressByScope: "Progress by Scope",
    qaStatusByScope: "QA Status by Scope",
    allTestsPassed: "All {{n}} tests passed",
    inProgressDone: "{{n}} in progress · {{done}}/{{total}} done",
    completeCount: "{{done}}/{{total}} complete",
    noQaTasks: "No QA tasks",
    betaApprovalNotice: "New tasks in Beta require ADMIN approval before creation.",
    feedbackByScope: "Feedback by Scope",
    allScopesClear: "All scopes clear — ready for release.",
    openItemsRemain: "{{n}} open items remain across scopes.",
    phaseChecklist: "Phase Checklist",
    openItemsByScope: "Open Items by Scope",
    openResolved: "{{open}} open · {{done}}/{{total}} resolved",
    phaseView: "{{phase}} view",
    allProjects: "All Projects",
    open: "Open",
    status: {
      draft: "Draft",
      inReview: "In Review",
      pending: "Pending",
      clarified: "Clarified",
      planned: "Planned",
      tasksGenerated: "Tasks Generated",
      implemented: "Implemented",
      qaPassed: "QA Passed",
      humanTested: "Human Tested",
      betaCleared: "Beta Cleared",
      released: "Released",
      deferred: "Deferred",
    },
    doctype: {
      tasks: "Tasks",
      datamodel: "Data Model",
      quickstart: "Quickstart",
      checklist: "Checklist",
      contracts: "Contracts",
      research: "Research",
      overview: "Overview",
      stackStyle: "Stack & Style",
    },
    issuetype: {
      bug: "Bug",
      feature: "Feature",
      enhancement: "Enhancement",
      task: "Task",
    },
  },
  helpCatalog: {
    ui: {
      included: "Included",
      upgrade: "Upgrade",
      admin: "Admin",
      adminOnly: "Admin only",
      howToLabel: "How to:",
      howToUseIt: "How to use it",
      availableFromPrefix: "Available from the ",
      availableFromSuffix: " plan.",
      yourPlan: "Your plan",
      subscriptionPlan: "Subscription plan",
    },
    category: {
      coreWorkspace: "Core workspace",
      developmentLifecycle: "Development lifecycle",
      integrations: "Integrations",
      teamCollaboration: "Team & collaboration",
      timeBillingConsulting: "Time, billing & consulting",
      adminConfiguration: "Admin & configuration",
      securityCompliance: "Security & compliance",
      enterpriseSupport: "Enterprise & support",
    },
    plan: {
      SINGLE_USER: {
        label: "Single User",
        userLimit: "1 user",
      },
      SMALL_BUSINESS: {
        label: "Small/Medium Business",
        userLimit: "Up to 5 users",
      },
      CONSULTANT: {
        label: "Consultant",
        userLimit: "Unlimited team",
      },
      ENTERPRISE: {
        label: "Enterprise",
        userLimit: "25+ users",
      },
    },
    feature: {
      dashboard: {
        title: "Dashboard",
        description: "Your project overview — lifecycle-phase progress per project plus quick links to each project's generated documents.",
        howTo: "Open Dashboard from the sidebar. Each project card shows its phase progress and document links; ADMINs get a New button to create a project inline.",
      },
      projects: {
        title: "Projects",
        description: "Create and manage projects. Unlimited projects on every paid plan.",
        howTo: "Create a project from the dashboard New button or the Projects nav, then open it to see its tabs (overview, lifecycle, tasks).",
      },
      tasks: {
        title: "My Tasks (Kanban)",
        description: "Your assigned work on a Kanban board across projects.",
        howTo: "Open My Tasks; drag cards between columns to change status, or open a card for detail.",
      },
      settings: {
        title: "Settings",
        description: "Account, company, and preference settings, including language.",
        howTo: "Open Settings from the nav; the language switcher and profile live here.",
      },
      faq: {
        title: "FAQ",
        description: "Answers to common questions about using wxKanban.",
        howTo: "Open FAQ from the nav.",
      },
      lifecycle: {
        title: "6-Phase Lifecycle & Orchestrator",
        description: "The Design → Implementation → QA → Human Testing → Beta → Release methodology that gates every feature and keeps a single source of truth for where a project stands.",
        howTo: "Drive it from Claude Code + the wxKanban kit in VS Code (buildscope → createSpecs → implement). The right command is available only at the right phase.",
      },
      specs: {
        title: "Specs & Scopes",
        description: "Scope-of-project documents and the generated specs, plans, and tasks for each feature.",
        howTo: "Author a scope with buildscope; generate the spec/plan/tasks with createSpecs; track them in the Cockpit.",
      },
      audit: {
        title: "Audit Reports",
        description: "A per-release audit trail of what was built, tested, and code-fenced.",
        howTo: "Open the Audit Report link on a project card, or regenerate it from the Admin dashboard.",
      },
      cockpit: {
        title: "Dev Cockpit",
        description: "A VS Code sidebar showing remaining work per scope, live as work is built.",
        howTo: "Install the Dev Cockpit extension; it pins your active scope and lists incomplete tasks.",
      },
      pmIntegrations: {
        title: "PM Integrations",
        description: "Connect Jira, Monday.com, Asana, and Trello and sync tasks two-way.",
        howTo: "Open PM Integrations and connect via OAuth; syncing runs on a background schedule.",
      },
      team: {
        title: "Team Management",
        description: "Invite teammates, assign roles, and manage access with role-based controls.",
        howTo: "Open Team; invite by email and set each member's role.",
      },
      timeTracking: {
        title: "Time Tracking & Billing",
        description: "Built-in timer with billable-hours tracking and time entries.",
        howTo: "Start the timer on a task; mark entries billable for invoicing.",
      },
      invoices: {
        title: "Invoices",
        description: "Generate PDF invoices from billable time entries.",
        howTo: "Open Invoices, pick a project and date range, and generate; invoiced entries are marked to prevent duplicates.",
      },
      reports: {
        title: "Reports",
        description: "Advanced reporting across time, projects, and outcomes.",
        howTo: "Open Reports from the nav.",
      },
      workHub: {
        title: "Work Hub",
        description: "A consultant engagement hub for managing client work.",
        howTo: "Open Work Hub to see and manage your engagements.",
      },
      clientPortal: {
        title: "Client Portal",
        description: "Give clients a scoped, read-only view of their project.",
        howTo: "Enable the portal for a project and share the client link.",
      },
      customWorkflows: {
        title: "Custom Workflows",
        description: "Tailor lifecycle workflows to your team's process.",
        howTo: "Configure custom workflows in project settings.",
      },
      apiAccess: {
        title: "API Access",
        description: "Programmatic access to your wxKanban data via API.",
        howTo: "Create an API token in Settings and call the documented endpoints.",
      },
      stackProfiles: {
        title: "Stack Profiles",
        description: "Reusable target-stack and style definitions applied when a project is created.",
        howTo: "Open Stack Profiles (Admin) to define or edit your company's stacks.",
      },
      themeProfiles: {
        title: "Theme Profiles",
        description: "Reusable look-and-feel themes for generated output.",
        howTo: "Open Theme Profiles (Admin) to define or edit themes.",
      },
      encryption: {
        title: "Encryption at rest",
        description: "Secrets are encrypted at rest with AES-256-GCM.",
        howTo: "Always on — no configuration required.",
      },
      soc2: {
        title: "SOC 2 & HIPAA",
        description: "SOC 2- and HIPAA-aligned controls and compliance tooling.",
        howTo: "Add your company's SDLC documents for compliance analysis; assign controls to team members.",
      },
      sso: {
        title: "Single Sign-On (SSO)",
        description: "Enterprise SSO for your organization.",
        howTo: "Contact your wxKanban representative to configure your identity provider.",
      },
      advancedSecurity: {
        title: "Advanced Security",
        description: "Additional enterprise-grade security controls.",
        howTo: "Configured as part of Enterprise onboarding.",
      },
      emailSupport: {
        title: "Email Support",
        description: "Email-based product support.",
        howTo: "Email admin@wxperts.com.",
      },
      dedicatedSupport: {
        title: "Dedicated Support",
        description: "A dedicated support contact for your account.",
        howTo: "Assigned during Enterprise onboarding.",
      },
      customIntegrations: {
        title: "Custom Integrations",
        description: "Bespoke integrations built for your organization.",
        howTo: "Scope custom integrations with your wxKanban representative.",
      },
      sla: {
        title: "SLA Guarantee",
        description: "A contractual uptime and response-time SLA.",
        howTo: "Part of your Enterprise agreement.",
      },
    },
  },
  // [SCOPE 052] Public Help/Tutorials pages (/docs, /docs/:slug)
  docs: {
    title: "Help & Tutorials",
    backHome: "← Back to home",
    sectionsLabel: "Help sections",
    navFaq: "FAQ",
    navVideos: "Videos",
    navSessions: "Training Sessions",
    navGuides: "Guides",
    guidesHeading: "Guides",
    guidesSubtitle: "Downloadable how-to guides you can keep alongside your project.",
    guidesConversionTitle: "WINDEV / WEBDEV Conversion Guide",
    guidesConversionSummary: "How to bring a legacy WINDEV or WEBDEV application across to a modern React and PostgreSQL stack, step by step.",
    guidesDraftBadge: "Draft",
    guidesDraftNote: "This guide is still a draft. The steps are usable, but wording and detail may change.",
    guidesLoading: "Loading guides…",
    guidesEmpty: "No guides are available yet.",
    guidesLanguageLabel: "Language",
    guidesLanguageAll: "All languages",
    faqHeading: "Frequently Asked Questions",
    faqSubtitle: "Answers to common questions about wxKanban.",
    faqEmpty: "No FAQs are available yet.",
    sessionsHeading: "Upcoming Online Training Sessions",
    sessionsSubtitle: "Join a live, instructor-led session to get up and running fast.",
    sessionsEmpty: "No sessions are scheduled right now.",
    sessionsRegister: "Register your interest and we'll notify you when the next session opens.",
    sessionsCta: "Contact us to register",
    sessionsJoin: "Register",
    videosHeading: "Training Videos",
    videosSubtitle: "Watch step-by-step walkthroughs of wxKanban.",
    videosEmpty: "No training videos are available yet.",
    loading: "Loading…",
    loadError: "Could not load documentation. Please try again later.",
    backAll: "← All documentation",
    detailError: "Could not load this document. Please try again later.",
    notFoundTitle: "Not found",
    notFoundBody: "This document does not exist or is not yet published.",
  },
  // [SCOPE 005 / T019] Time & Billing (SPEC-005). "Resource", never "AI" (constraint 5).
  timeBilling: {
    common: {
      cancel: "Cancel",
      close: "Close",
      delete: "Delete",
      edit: "Edit",
      loading: "Loading…",
      save: "Save",
      saving: "Saving…",
    },
    page: {
      title: "Time & Billing",
      open: "Open Time & Billing",
      manualOnly: "Resource time from agent runs is not available yet. Enter Resource time manually.",
      engagedManual: "Client projects you are engaged on take Resource time by manual entry only.",
      generateStatement: "Generate statement",
      billedTotal: "Billed projects in this range: {{hours}} hrs",
      actionFailed: "That did not work. Please try again.",
      loadFailed: "Could not load your projects.",
    },
    range: {
      from: "From",
      to: "To",
      thisWeek: "This week",
      includeInvoiced: "Include invoiced time",
      invalid: "The start date must be on or before the end date.",
    },
    projects: {
      none: "No projects yet.",
      clientProject: "Client: {{client}}",
      hours: "{{hours}} hrs",
      billThis: "Billable",
      entries: "Entries",
      rates: "Rates",
      client: "Client",
      noClient: "— No client —",
      billedTo: "Billed to {{client}}",
      assignClient: "Assign a client to bill this project",
    },
    lines: {
      line: "Line",
      scope: "Scope",
      developer: "Developer",
      resource: "Resource",
      developerHours: "Developer hrs",
      resourceHours: "Resource hrs",
      actions: "Actions",
      addDeveloper: "Add Developer time",
      addResource: "Add Resource time",
      noScopes: "This project has no scopes yet.",
      deletedScope: "Deleted scope",
      archived: "archived",
      brainstorming: "Brainstorming",
      processes: "Processes",
      dba: "DBA",
      management: "Management",
      meetings: "Meetings",
      projectTotal: "Project total",
    },
    entry: {
      addDeveloper: "Add Developer time",
      addResource: "Add Resource time",
      editTitle: "Edit time entry",
      person: "Who did the work",
      model: "Model",
      date: "Date",
      time: "Time",
      timeHelp: "Enter 1:38, 1.5 or 45m. A leading minus makes a correction.",
      savedAs: "Saved as {{hours}} hrs (rounded up to the next 15 minutes).",
      notes: "Notes",
      correctionNeedsNote: "A correction (negative time) needs a note.",
      saveFailed: "Could not save the entry.",
    },
    entries: {
      title: "Time entries — {{project}}",
      none: "No entries in this range.",
      who: "Who / model",
      hours: "Hrs",
      invoiced: "Invoiced",
      invoicedHelp: "This entry is on a statement and can no longer be changed. Add a correction in a later period.",
      confirmDelete: "Delete this entry?",
      deleteFailed: "Could not delete the entry.",
      loadFailed: "Could not load the entries.",
    },
    timer: {
      title: "Timer",
      idle: "No timer running. Press the clock on a project, or ▶ on a line, to start one.",
      start: "Start timer",
      startTitle: "Start timer",
      chooseLine: "Line",
      startHelp: "Each start and stop saves its own entry, so you can log several timeframes in one day.",
      runningHere: "The timer is running on this project.",
      stop: "Stop and save",
      alreadyRunning: "A timer is already running.",
      willSave: "Will save as {{hours}} hrs.",
      autoNotes: "Agent runs during this timer",
      autoUnavailable: "Agent runs are not recorded yet.",
      noRuns: "No agent runs yet.",
      refresh: "Refresh agent runs",
      manualNotes: "Your notes",
      manualPlaceholder: "e.g. 15-minute call with the client about the audit scope",
      notesFailed: "Could not load the agent runs.",
      stopFailed: "Could not stop the timer.",
      pause: "Pause",
      resume: "Resume",
      paused: "Paused — this time is not billed.",
      pauseFailed: "Could not pause or resume the timer.",
    },
    statement: {
      title: "Generate statement",
      description: "Create a statement for one client and a billing period.",
      customer: "Client",
      noBilledProjects: "This client has no billed projects yet",
      cycle: "Billing cycle",
      cycleWeekly: "Weekly",
      cycleBiweekly: "Bi-weekly",
      periodStart: "Period start",
      periodEnd: "Period end",
      periodNote: "Only billed, unlocked time in this period is included. Generated entries become read-only.",
      generate: "Generate statement",
      working: "Working…",
      created: "Statement {{name}} created",
      number: "Statement number",
      total: "Total",
      entries: "Entries",
      downloadPdf: "Download PDF",
      periodFailed: "Could not load the suggested period",
      cycleFailed: "Could not change the billing cycle",
      generateFailed: "Could not generate the statement",
      downloadFailed: "Could not download the PDF",
    },
    rates: {
      title: "Project rates",
      unchanged: "Leave blank to keep",
      useDefault: "Use the company default",
      help: "New entries use the rate in force. Existing entries keep the rate they were created with.",
      companyDefault: "company default",
      saved: "Saved. Developer: {{developer}} · Resource: {{resource}}",
      saveFailed: "Could not save the rates.",
    },
    settings: {
      title: "Time & Billing",
      description: "Default hourly rates and the bank details printed on client statements.",
      developerRate: "Developer rate ($/hr)",
      resourceRate: "Resource rate ($/hr)",
      ratesHelp: "A project can override either rate. Changing a rate does not change existing entries.",
      achTitle: "ACH payment details",
      achComplete: "Complete",
      achIncomplete: "Incomplete — statements will say ACH is not set up",
      bankName: "Bank name",
      accountName: "Account name",
      routingNumber: "Routing number",
      accountNumber: "Account number",
      endingIn: "ending in {{last4}}",
      notSet: "not set",
      replacePlaceholder: "Type a new number to replace it",
      achHelp: "Stored encrypted. Only the last 4 digits are shown here; the full numbers appear only on statements.",
      nothingChanged: "Nothing to save.",
      saved: "Saved.",
      saveFailed: "Could not save the settings.",
      loadFailed: "Could not load the settings.",
    },
  },
} as const;

export type LocaleBundle = typeof enUS;

// [SCOPE 052] Non-en bundles may be partial — any missing key falls back to en-US
// per key (FR-002). This lets string extraction (add keys to en-US) run ahead of
// translation without breaking the other bundles' types.
export type PartialLocaleBundle = {
  [P in keyof LocaleBundle]?: {
    [K in keyof LocaleBundle[P]]?: LocaleBundle[P][K] extends object
      ? Partial<LocaleBundle[P][K]>
      : LocaleBundle[P][K];
  };
};
